import React, { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Link } from 'react-router-dom';
import { NavbarDashbord } from './navbarDashbord';
import { getDataHistories, deleteHistory } from './action';
import { History as HistoryIcon, Trash2, Clock, User, Calendar, RefreshCcw, CheckCircle } from 'lucide-react';
import '../styles/admin.css';

export function PageHistorique() {
    const dispatch = useDispatch();
    const histories = useSelector(state => state.histories) || [];
    const loading = useSelector(state => state.loading);
    const [refreshing, setRefreshing] = useState(false);
    const [showToast, setShowToast] = useState(false);

    useEffect(() => {
        dispatch(getDataHistories());
    }, [dispatch]);

    const handleRefresh = () => {
        setRefreshing(true);
        dispatch(getDataHistories())
            .unwrap()
            .then(() => {
                setShowToast(true);
                setTimeout(() => setShowToast(false), 2000);
            })
            .catch((err) => {
                console.error("Refresh failed:", err);
            })
            .finally(() => {
                setRefreshing(false);
            });
    };

    const handleClearAll = () => {
        dispatch(deleteHistory('all'));
    };

    const handleDeleteOne = (id) => {
        dispatch(deleteHistory(id));
    };

    const formatDate = (dateStr) => {
        if (!dateStr) return 'Date inconnue';
        const date = new Date(dateStr);
        if (isNaN(date.getTime())) return 'Date inconnue';
        return date.toLocaleDateString('fr-FR', { day: '2-digit', month: 'long', year: 'numeric' });
    };

    const formatTime = (dateStr) => {
        if (!dateStr) return '--:--';
        const date = new Date(dateStr);
        if (isNaN(date.getTime())) return '--:--';
        return date.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
    };

    const groupedHistoriesArray = [];
    let currentLabel = null;
    let currentGroup = [];

    const today = new Date().toLocaleDateString('fr-FR');
    const yesterday = new Date(new Date().setDate(new Date().getDate() - 1)).toLocaleDateString('fr-FR');

    histories.forEach(item => {
        let dateStr = 'Date inconnue';
        if (item.created_at) {
            const date = new Date(item.created_at);
            if (!isNaN(date.getTime())) {
                dateStr = date.toLocaleDateString('fr-FR');
            }
        }
        
        let label = dateStr;
        if (dateStr === today) {
            label = "Aujourd'hui";
        } else if (dateStr === yesterday) {
            label = "Hier";
        } else if (dateStr !== 'Date inconnue') {
            const d = new Date(item.created_at);
            label = d.toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
            label = label.charAt(0).toUpperCase() + label.slice(1);
        } else {
            label = "Date inconnue";
        }

        if (label !== currentLabel) {
            if (currentLabel !== null) {
                groupedHistoriesArray.push({ label: currentLabel, items: currentGroup });
            }
            currentLabel = label;
            currentGroup = [item];
        } else {
            currentGroup.push(item);
        }
    });
    if (currentLabel !== null) {
        groupedHistoriesArray.push({ label: currentLabel, items: currentGroup });
    }

    return (
        <div className="admin-layout">
            <NavbarDashbord />
            <main className="admin-main-content">
                <header className="admin-header">
                    <div className="admin-title-group">
                        <h1>Journal d'Activités</h1>
                        <p>Suivez toutes les actions effectuées sur la plateforme en temps réel.</p>
                    </div>
                    <div className="admin-header-actions">
                        <button className="admin-btn-secondary" onClick={handleRefresh} disabled={refreshing || loading}>
                            <RefreshCcw size={18} className={loading || refreshing ? 'spin' : ''} />
                            <span>Actualiser</span>
                        </button>
                        <button className="admin-btn-delete" onClick={handleClearAll} disabled={histories.length === 0}>
                            <Trash2 size={18} />
                            <span>Effacer Tout</span>
                        </button>
                    </div>
                </header>

                <div className="history-container" style={{ marginTop: '30px' }}>
                    {histories.length === 0 ? (
                        <div className="empty-state-card">
                            <HistoryIcon size={64} style={{ opacity: 0.1, marginBottom: '20px' }} />
                            <h3 style={{ color: 'var(--admin-primary)', fontWeight: '800' }}>Aucune activité</h3>
                            <p style={{ color: 'var(--admin-text-muted)' }}>Les actions administratives apparaîtront ici.</p>
                        </div>
                    ) : (
                        <div className="history-timeline">
                            {groupedHistoriesArray.map((group, gIdx) => (
                                <div key={gIdx} className="history-group" style={{ marginBottom: '30px' }}>
                                    <h3 style={{ 
                                        color: 'var(--admin-primary)', 
                                        fontWeight: '800', 
                                        fontSize: '1.1rem', 
                                        marginBottom: '15px',
                                        paddingBottom: '10px',
                                        borderBottom: '2px solid var(--admin-border)'
                                    }}>{group.label}</h3>
                                    
                                    {group.items.map((item, idx) => (
                                        <div key={item.id} className="history-item-card" style={{ 
                                            background: 'white', 
                                            padding: '20px 25px', 
                                            borderRadius: '18px', 
                                            marginBottom: '15px', 
                                            display: 'flex', 
                                            alignItems: 'center', 
                                            justifyContent: 'space-between',
                                            border: '1px solid var(--admin-border)',
                                            boxShadow: 'var(--admin-shadow-sm)',
                                            position: 'relative'
                                        }}>
                                            <div style={{ display: 'flex', alignItems: 'center', gap: '20px' }}>
                                                <div className="history-indicator" style={{ 
                                                    width: '4px', 
                                                    height: '40px', 
                                                    backgroundColor: item.color || 'var(--admin-primary)',
                                                    borderRadius: '10px'
                                                }}></div>
                                                
                                                <div className="history-main-info">
                                                    <h4 style={{ margin: 0, fontSize: '0.95rem', fontWeight: '800', color: 'var(--admin-primary)' }}>{item.title}</h4>
                                                    <div style={{ display: 'flex', alignItems: 'center', gap: '15px', marginTop: '5px', fontSize: '0.75rem', color: 'var(--admin-text-muted)' }}>
                                                        <span style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                                                            <User size={12} /> {item.user}
                                                        </span>
                                                        <span style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                                                            <Calendar size={12} /> {formatDate(item.created_at)}
                                                        </span>
                                                        <span style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                                                            <Clock size={12} /> {item.time || formatTime(item.created_at)}
                                                        </span>
                                                    </div>
                                                </div>
                                            </div>

                                            <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
                                                {item.amount && (
                                                    <span style={{ 
                                                        fontWeight: '900', 
                                                        fontSize: '0.9rem', 
                                                        color: 'var(--admin-secondary)',
                                                        background: 'rgba(112, 0, 0, 0.05)',
                                                        padding: '4px 12px',
                                                        borderRadius: '10px'
                                                    }}>{item.amount}</span>
                                                )}
                                                
                                                <div className="history-actions" style={{ display: 'flex', gap: '8px' }}>
                                                    <button className="history-action-btn delete" onClick={() => handleDeleteOne(item.id)} title="Supprimer">
                                                        <Trash2 size={16} />
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </main>
            {showToast && (
                <div style={{
                    position: 'fixed',
                    bottom: '30px',
                    right: '30px',
                    backgroundColor: '#10b981',
                    color: 'white',
                    padding: '12px 24px',
                    borderRadius: '12px',
                    boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.2)',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '10px',
                    zIndex: 9999,
                    fontWeight: '600',
                    fontSize: '0.9rem'
                }}>
                    <CheckCircle size={18} />
                    <span>Journal d'activités actualisé !</span>
                </div>
            )}
        </div>
    );
}
